import React from 'react';
import { useDirtySnapshot, useDiscardGuard } from '../../../../hooks/useDiscardGuard';
import {
  ArrowLeft,
  X,
  ChevronRight,
  Minus,
  Plus,
  Download,
} from 'lucide-react';
import type { Lead, LeadPipeline, LeadPipelineStage } from '../../../../types';
import type { LeadOutcome } from '../../../../utils/leadPipeline';
import { PdfDocumentViewer } from '../../../../components/PdfDocumentViewer';
import { SpreadsheetViewer } from '../../../../components/SpreadsheetViewer';
import { PdfPrintButton } from '../../../../components/PdfPrintButton';
import { PdfMailShareButton } from '../../../../components/PdfMailShareButton';
import { leadDocumentMailShareOptions } from '../../utils/documentMailDraft';
import { spreadsheetPreviewKind } from '../../../../utils/spreadsheetPreview';
import {
  getLeadDocumentUrl,
  isImageDocument,
  isPdfDocument,
  type LeadDocument,
} from '../../../projects/utils/leadDocumentFiles';
import { ChooseOfferingForDocumentModal } from '../../../projects/components/ChooseOfferingForDocumentModal';
import {
  ProjectActivityFormFields,
  type ProjectActivityFormState,
} from '../../../../components/activities/ProjectActivityFormFields';
import type { UserMultiSelectOption } from '../../../../components/UserMultiSelect';
import type { EmployeeMultiSelectOption } from '../../../../components/employees/EmployeeMultiSelect';
import { LeadLostModal, type LeadLostPayload } from '../LeadLostModal';
import { CategoryChangeStageModal } from '../CategoryChangeStageModal';
import { ActivityFormWithSideCalendar } from '../../../../components/activities/activityDayCalendar';
import { LEAD_DOCUMENT_TYPES } from './leadActivityHelpers';
import { LEAD_DETAIL_BTN_OUTLINE, LEAD_DETAIL_BTN_PRIMARY } from './leadDetailStyles';

/** Aktionen im Kopf der Dokument-Vorschau: ohne Rahmen, nur Hover-Fläche. */
const PREVIEW_HEADER_BTN =
  'inline-flex h-9 items-center justify-center gap-2 shrink-0 rounded-md px-3 text-sm font-medium text-app-text-muted hover:bg-app-hover hover:text-app-text disabled:opacity-40 transition-colors';
import { UI_Z_INDEX } from '../../../../utils/uiZIndex';
import { isDocumentTypeAvailable } from '../../../../lib/documentTypeModules';
import { useModuleAccess } from '../../../../hooks/useModuleAccess';
import type { LeadActivity } from '../../../../pages/LeadDetailPage';

const ACTIVITY_MODAL_CLOSE_BTN =
  'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-app-text-subtle hover:bg-app-hover hover:text-app-text';

type ActivityTypeOption = { id: string; label: string; icon: string; default_checklist_id?: string | null };

/** Alle Lead-Detail-Modals: Angebot wählen, Dokument erstellen, Vorschau, Aktivität bearbeiten/planen, Verloren, Phase wählen. */
/**
 * Nutzereingaben im Aktivitäts-Formular. Beim Anlegen setzt der Hook Datum, Uhrzeit, Typ und
 * Checkliste selbst nach (Zeitzone, Standardtyp) — die zählen dort nicht; beim Bearbeiten schon.
 */
function activityInputSnapshot(form: ProjectActivityFormState, includeSchedule: boolean): string {
  return JSON.stringify([
    form.title.trim(),
    form.notes.trim(),
    // Adresse hängt am Hook-State, nicht am Formular-Typ.
    String((form as { site_address?: string }).site_address ?? '').trim(),
    form.scheduling_status,
    form.assignee_user_ids,
    form.assignee_employee_ids,
    form.recurrence?.freq ?? 'none',
    ...(includeSchedule
      ? [form.dueDate, form.dueTimeStart, form.dueTimeEnd, form.endDate, form.activity_type, form.checklist_id]
      : []),
  ]);
}

export function LeadDetailModals({
  lead,
  id,
  isMobileView,
  // Angebot wählen (Dokument erstellen)
  showOfferingPickerModal,
  setShowOfferingPickerModal,
  pendingDocumentTypeLabel,
  setPendingDocumentTypeLabel,
  documentTypeLabel,
  offeringPickerFromCreateDoc,
  setOfferingPickerFromCreateDoc,
  offerEditorOpen,
  navigateToLeadDetail,
  reloadLead,
  openDocumentEditor,
  setShowCreateDocumentModal,
  // Dokumenttyp wählen ("Neues Dokument erstellen")
  showCreateDocumentModal,
  // Dokument-Vorschau
  previewDocument,
  setPreviewDocument,
  previewZoom,
  setPreviewZoom,
  // Aktivität bearbeiten / anlegen
  editingActivity,
  activitySaveError,
  resetActivityForm,
  activityForm,
  setActivityForm,
  activityTypesList,
  activityChecklistOptions,
  activityUserSelectOptions,
  activityEmployeeSelectOptions,
  activityAddressFieldEl,
  activityDayCalendarEl,
  updateActivity,
  addingActivity,
  showCreateActivityModal,
  setShowCreateActivityModal,
  addActivity,
  // Verloren
  showVerlorenModal,
  setShowVerlorenModal,
  setVerlorenError,
  handleVerlorenSave,
  isSaving,
  verlorenError,
  // Kategorie-/Phasenwechsel
  categoryChangePending,
  setCategoryChangePending,
  setCategoryChangeError,
  leadPipelineStages,
  leadOutcome,
  categoryChangeError,
  applyLeadCategoryChange,
}: {
  lead: Lead | null;
  id: string | undefined;
  isMobileView: boolean;
  showOfferingPickerModal: boolean;
  setShowOfferingPickerModal: React.Dispatch<React.SetStateAction<boolean>>;
  pendingDocumentTypeLabel: (typeof LEAD_DOCUMENT_TYPES)[number] | null;
  setPendingDocumentTypeLabel: React.Dispatch<React.SetStateAction<(typeof LEAD_DOCUMENT_TYPES)[number] | null>>;
  documentTypeLabel: (typeof LEAD_DOCUMENT_TYPES)[number];
  offeringPickerFromCreateDoc: boolean;
  setOfferingPickerFromCreateDoc: React.Dispatch<React.SetStateAction<boolean>>;
  offerEditorOpen: boolean;
  navigateToLeadDetail: () => void;
  reloadLead: () => Promise<void>;
  openDocumentEditor: (documentLabel: (typeof LEAD_DOCUMENT_TYPES)[number], offeringId: string) => void;
  setShowCreateDocumentModal: React.Dispatch<React.SetStateAction<boolean>>;
  showCreateDocumentModal: boolean;
  previewDocument: LeadDocument | null;
  setPreviewDocument: React.Dispatch<React.SetStateAction<LeadDocument | null>>;
  previewZoom: number;
  setPreviewZoom: React.Dispatch<React.SetStateAction<number>>;
  editingActivity: LeadActivity | null;
  activitySaveError: string | null;
  resetActivityForm: () => void;
  activityForm: ProjectActivityFormState;
  setActivityForm: React.Dispatch<React.SetStateAction<ProjectActivityFormState>>;
  activityTypesList: ActivityTypeOption[];
  activityChecklistOptions: { value: string; label: string }[];
  activityUserSelectOptions: UserMultiSelectOption[];
  activityEmployeeSelectOptions: EmployeeMultiSelectOption[];
  activityAddressFieldEl: React.ReactNode;
  activityDayCalendarEl: React.ReactNode;
  updateActivity: (activityId: string) => void | Promise<void>;
  addingActivity: boolean;
  showCreateActivityModal: boolean;
  setShowCreateActivityModal: React.Dispatch<React.SetStateAction<boolean>>;
  addActivity: () => Promise<boolean> | void;
  showVerlorenModal: boolean;
  setShowVerlorenModal: React.Dispatch<React.SetStateAction<boolean>>;
  setVerlorenError: React.Dispatch<React.SetStateAction<string | null>>;
  handleVerlorenSave: (payload: LeadLostPayload) => Promise<void>;
  isSaving: boolean;
  verlorenError: string | null;
  categoryChangePending: { categoryKey: string; categoryLabel: string; pipeline: LeadPipeline } | null;
  setCategoryChangePending: React.Dispatch<
    React.SetStateAction<{ categoryKey: string; categoryLabel: string; pipeline: LeadPipeline } | null>
  >;
  setCategoryChangeError: React.Dispatch<React.SetStateAction<string | null>>;
  leadPipelineStages: LeadPipelineStage[];
  leadOutcome: LeadOutcome;
  categoryChangeError: string | null;
  applyLeadCategoryChange: (nextKey: string, stageId?: string) => Promise<boolean>;
}) {
  // Jeder Belegtyp nur mit seinem Modul (Angebote, Auftragsabwicklung, Rechnungen).
  const { loaded: modulesLoaded, modules } = useModuleAccess();
  const documentTypeOptions = LEAD_DOCUMENT_TYPES.filter(
    (label) => modulesLoaded && isDocumentTypeAvailable(label, modules),
  );
  // Verklicken neben das Aktivitäts-Fenster darf Eingaben nicht stillschweigend verwerfen.
  const activityModalOpen = showCreateActivityModal || Boolean(editingActivity);
  const activityMode: 'create' | 'edit' = editingActivity ? 'edit' : 'create';
  // Basis beim Öffnen bzw. Wechsel der Aktivität, nicht bei jeder Eingabe.
  const activityDirty = useDirtySnapshot(
    activityInputSnapshot(activityForm, activityMode === 'edit'),
    `${activityModalOpen}:${editingActivity?.id ?? ''}`,
  );
  const activityDiscardGuard = useDiscardGuard({
    dirty: activityModalOpen && activityDirty,
    onClose: () => {
      if (activityMode === 'create') setShowCreateActivityModal(false);
      resetActivityForm();
    },
    disabled: addingActivity,
    description:
      activityMode === 'edit'
        ? 'Deine Änderungen an der Aktivität sind noch nicht gespeichert.'
        : 'Die Aktivität ist noch nicht angelegt. Deine Eingaben gehen verloren.',
  });

  return (
    <>
      <ChooseOfferingForDocumentModal
        open={showOfferingPickerModal && Boolean(lead && id)}
        documentTypeLabel={pendingDocumentTypeLabel ?? documentTypeLabel}
        projectRouteId={id!}
        projectOfferings={lead?.offerings ?? []}
        onClose={() => {
          setShowOfferingPickerModal(false);
          setOfferingPickerFromCreateDoc(false);
          setPendingDocumentTypeLabel(null);
          if (offerEditorOpen && lead) navigateToLeadDetail();
        }}
        onBack={
          offeringPickerFromCreateDoc
            ? () => {
                setShowOfferingPickerModal(false);
                setShowCreateDocumentModal(true);
              }
            : undefined
        }
        onChosen={async (offeringId) => {
          await reloadLead();
          openDocumentEditor(pendingDocumentTypeLabel ?? documentTypeLabel, offeringId);
        }}
        onOfferingsChange={reloadLead}
      />

      {showCreateDocumentModal && (
        <div
          className="fixed inset-0 z-[230] flex items-center justify-center p-4 bg-black/40 backdrop-blur-[2px]"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowCreateDocumentModal(false);
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="create-doc-modal-title"
            className="bg-app-surface rounded-md w-full max-w-lg shadow-2xl border border-app-border overflow-hidden flex flex-col max-h-[min(90vh,640px)]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-3 border-b border-app-border flex items-center gap-3 bg-app-surface-muted/80 shrink-0">
              <button
                type="button"
                onClick={() => setShowCreateDocumentModal(false)}
                className="p-1.5 rounded-lg text-app-text-subtle hover:bg-slate-200 hover:text-app-text transition-colors"
                aria-label="Schließen"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <h2 id="create-doc-modal-title" className="text-base font-bold text-app-text flex-1 min-w-0">
                Neues Dokument erstellen
              </h2>
              <button
                type="button"
                onClick={() => setShowCreateDocumentModal(false)}
                className="p-1.5 rounded-lg text-app-text-subtle hover:bg-slate-200 hover:text-app-text transition-colors shrink-0"
                aria-label="Schließen"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="overflow-y-auto p-3 sm:p-4 space-y-2">
              {documentTypeOptions.map((label) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => {
                    setShowCreateDocumentModal(false);
                    setPendingDocumentTypeLabel(label);
                    setOfferingPickerFromCreateDoc(true);
                    setShowOfferingPickerModal(true);
                  }}
                  className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-lg border border-app-border bg-app-surface text-left text-sm font-semibold text-app-text hover:bg-app-hover hover:border-app-border-strong transition-colors"
                >
                  <span>{label}</span>
                  <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {previewDocument && lead && (
        <div
          className="fixed inset-0 z-[240] flex items-center justify-center bg-black/50 p-4"
          onClick={() => setPreviewDocument(null)}
        >
          <div
            className="bg-app-surface rounded-xl shadow-xl w-full max-w-7xl h-[92vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-app-border">
              <h3 className="text-sm sm:text-base font-semibold text-app-text truncate pr-3">
                {previewDocument.filename}
              </h3>
              <div className="flex items-center gap-1.5">
                {isImageDocument(previewDocument) && (
                  <>
                    <button
                      type="button"
                      onClick={() => setPreviewZoom((z) => Math.max(0.5, Number((z - 0.1).toFixed(2))))}
                      className="p-1.5 rounded-md text-app-text-subtle hover:bg-app-hover hover:text-app-text-muted"
                      aria-label="Herauszoomen"
                      title="Herauszoomen"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setPreviewZoom(1)}
                      className="px-2.5 py-1 rounded-md text-xs font-medium text-app-text-muted hover:bg-app-hover"
                      aria-label="Zoom zurücksetzen"
                      title="Zoom zurücksetzen"
                    >
                      {Math.round(previewZoom * 100)}%
                    </button>
                    <button
                      type="button"
                      onClick={() => setPreviewZoom((z) => Math.min(4, Number((z + 0.1).toFixed(2))))}
                      className="p-1.5 rounded-md text-app-text-subtle hover:bg-app-hover hover:text-app-text-muted"
                      aria-label="Hereinzoomen"
                      title="Hereinzoomen"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </>
                )}
                {isPdfDocument(previewDocument) ? (
                  <>
                    <PdfMailShareButton
                      {...leadDocumentMailShareOptions(lead.id, previewDocument)}
                      onBeforeOpen={() => setPreviewDocument(null)}
                      className={PREVIEW_HEADER_BTN}
                    />
                    <PdfPrintButton
                      url={getLeadDocumentUrl(lead.id, previewDocument)}
                      className={PREVIEW_HEADER_BTN}
                    />
                  </>
                ) : null}
                <a
                  href={getLeadDocumentUrl(lead.id, previewDocument, { download: true })}
                  className={PREVIEW_HEADER_BTN}
                >
                  <Download className="w-4 h-4" />
                  Download
                </a>
                <button
                  type="button"
                  onClick={() => setPreviewDocument(null)}
                  className="p-1.5 rounded-md text-app-text-subtle hover:bg-app-hover hover:text-app-text-muted"
                  aria-label="Vorschau schließen"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="flex-1 min-h-0 bg-app-surface-muted">
              {isImageDocument(previewDocument) ? (
                <div className="w-full h-full bg-app-surface flex items-center justify-center overflow-auto">
                  <img
                    src={getLeadDocumentUrl(lead.id, previewDocument)}
                    alt={previewDocument.filename}
                    className="h-full w-auto max-w-none object-contain"
                    style={{ transform: `scale(${previewZoom})`, transformOrigin: 'center center' }}
                  />
                </div>
              ) : isPdfDocument(previewDocument) ? (
                <PdfDocumentViewer
                  url={getLeadDocumentUrl(lead.id, previewDocument)}
                  title={previewDocument.filename}
                  className="h-full w-full"
                />
              ) : spreadsheetPreviewKind(previewDocument.filename, previewDocument.mime_type) ? (
                <SpreadsheetViewer
                  url={getLeadDocumentUrl(lead.id, previewDocument)}
                  filename={previewDocument.filename}
                  kind={spreadsheetPreviewKind(previewDocument.filename, previewDocument.mime_type)!}
                  className="h-full w-full"
                />
              ) : (
                <div className="h-full flex flex-col items-center justify-center gap-3 text-center px-6">
                  <p className="text-app-text-muted text-sm">
                    Für diesen Dateityp ist keine Inline-Vorschau verfügbar.
                  </p>
                  <a
                    href={getLeadDocumentUrl(lead.id, previewDocument)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex h-9 items-center rounded-md bg-pipedrive-blue px-3.5 text-sm font-medium text-white hover:bg-blue-600"
                  >
                    In neuem Tab öffnen
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal: Aktivität bearbeiten */}
      {editingActivity && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          {...activityDiscardGuard.backdropProps}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="edit-activity-title"
            className="flex h-[min(90vh,860px)] max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl border border-app-border bg-app-surface shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <ActivityFormWithSideCalendar
              className="h-full min-h-0 flex-1 overflow-hidden"
              calendarFrom="sm"
              formColumn={
                <>
                <div className="flex shrink-0 items-center justify-between gap-3 px-5 pt-4 pb-1">
                  <h3 id="edit-activity-title" className="text-lg font-semibold text-app-text">Aktivität bearbeiten</h3>
                  <button
                    type="button"
                    onClick={activityDiscardGuard.requestClose}
                    className={ACTIVITY_MODAL_CLOSE_BTN}
                    aria-label="Schließen"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <div className="app-scrollbar min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-4">
                <ProjectActivityFormFields
                  form={activityForm}
                  setForm={setActivityForm}
                  activityTypesList={activityTypesList}
                  checklistOptions={activityChecklistOptions}
                  userSelectOptions={activityUserSelectOptions}
                  employeeSelectOptions={activityEmployeeSelectOptions}
                  addressField={activityAddressFieldEl}
                  dropdownZIndex={UI_Z_INDEX.portalDropdownInModal}
                />
                </div>
                {activitySaveError ? (
                  <p className="px-5 text-sm text-red-600">{activitySaveError}</p>
                ) : null}
                <div className="flex shrink-0 justify-end gap-2 border-t border-app-border px-5 py-3">
                  <button type="button" onClick={resetActivityForm} className={LEAD_DETAIL_BTN_OUTLINE}>
                    Abbrechen
                  </button>
                  <button
                    type="button"
                    onClick={() => editingActivity && updateActivity(editingActivity.id)}
                    disabled={addingActivity || !activityForm.title.trim()}
                    className={LEAD_DETAIL_BTN_PRIMARY}
                  >
                    {addingActivity ? 'Speichert …' : 'Änderungen speichern'}
                  </button>
                </div>
                </>
              }
              calendar={activityDayCalendarEl}
            />
          </div>
        </div>
      )}

      {/* Modal: Aktivität planen (neu anlegen) */}
      {showCreateActivityModal && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4 bg-black/50"
          {...activityDiscardGuard.backdropProps}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="create-activity-title"
            className="flex h-[min(92vh,860px)] max-h-[92vh] w-full flex-col overflow-hidden rounded-t-2xl border-0 bg-app-surface shadow-xl sm:h-[min(90vh,860px)] sm:max-h-[90vh] sm:max-w-5xl sm:rounded-xl sm:border sm:border-app-border"
            onClick={(e) => e.stopPropagation()}
          >
            <ActivityFormWithSideCalendar
              className="h-full min-h-0 flex-1 overflow-hidden"
              calendarFrom="sm"
              formColumn={
                <>
                <div className="flex shrink-0 items-center justify-between gap-3 px-5 pt-4 pb-1">
                  <h3 id="create-activity-title" className="text-lg font-semibold text-app-text">Aktivität planen</h3>
                  <button
                    type="button"
                    onClick={activityDiscardGuard.requestClose}
                    className={ACTIVITY_MODAL_CLOSE_BTN}
                    aria-label="Schließen"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <div className="app-scrollbar min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-4">
                <ProjectActivityFormFields
                  form={activityForm}
                  setForm={setActivityForm}
                  activityTypesList={activityTypesList}
                  checklistOptions={activityChecklistOptions}
                  userSelectOptions={activityUserSelectOptions}
                  employeeSelectOptions={activityEmployeeSelectOptions}
                  addressField={activityAddressFieldEl}
                  descriptionRows={2}
                  compactTypePills={isMobileView}
                  syncEndDateWithStartDate={isMobileView}
                  dropdownZIndex={UI_Z_INDEX.portalDropdownInModal}
                />
                </div>
                <div className="flex shrink-0 justify-end gap-2 border-t border-app-border px-5 py-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateActivityModal(false);
                      resetActivityForm();
                    }}
                    className={LEAD_DETAIL_BTN_OUTLINE}
                  >
                    Abbrechen
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      const ok = await addActivity();
                      if (ok) setShowCreateActivityModal(false);
                    }}
                    disabled={addingActivity || !activityForm.title.trim()}
                    className={LEAD_DETAIL_BTN_PRIMARY}
                  >
                    {addingActivity ? 'Speichert …' : 'Speichern'}
                  </button>
                </div>
                </>
              }
              calendar={activityDayCalendarEl}
            />
          </div>
        </div>
      )}

      {activityDiscardGuard.dialog}

      <LeadLostModal
        open={showVerlorenModal}
        onClose={() => {
          setShowVerlorenModal(false);
          setVerlorenError(null);
        }}
        onConfirm={handleVerlorenSave}
        isSaving={isSaving}
        error={verlorenError}
      />

      <CategoryChangeStageModal
        open={categoryChangePending != null}
        onClose={() => {
          if (isSaving) return;
          setCategoryChangePending(null);
          setCategoryChangeError(null);
        }}
        categoryLabel={categoryChangePending?.categoryLabel ?? ''}
        pipeline={categoryChangePending?.pipeline ?? null}
        currentStageLabel={
          leadPipelineStages.find((s) => s.id === lead?.stage_id)?.label ??
          (leadOutcome === 'open' && typeof lead?.status === 'string' ? lead.status : null)
        }
        isSaving={isSaving}
        error={categoryChangeError}
        onConfirm={async (stageId) => {
          if (!categoryChangePending) return;
          await applyLeadCategoryChange(categoryChangePending.categoryKey, stageId);
        }}
      />
    </>
  );
}
